import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useUserProfiles } from 'context/UserProfilesContext';

// ---------------------------------------------------------------------------
// InvitePanel
//
// v1 of "bringing your team into EduTracker Pro". Self-service account
// creation isn't wired up yet (adding it means either exposing
// supabase.auth.signUp — which lets anyone in the world claim a seat —
// or a proper invite endpoint that requires the service_role key we
// deliberately don't ship to the browser). Until then, this panel does
// two things:
//
//   1. Explains the two-step process in plain language: your admin
//      creates the account in Supabase, then the teammate signs in at
//      the URL below. This IS how a real district would onboard staff.
//
//   2. Provides copy buttons for the app URL AND a ready-to-paste
//      invitation email so a teacher can invite an OT in one tap.
//
// When self-service signup does land, this component becomes the entry
// point for it — the "Send invite" button gets a real backend and the
// two-step language disappears.
// ---------------------------------------------------------------------------

const InvitePanel = () => {
  const { isLive } = useUserProfiles();
  const [copied, setCopied] = useState(null); // 'url' | 'email'

  const appUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/login`;
  }, []);

  const emailTemplate = useMemo(() => {
    return [
      "Hi there —",
      "",
      "You've been added to our special-ed team's EduTracker Pro workspace.",
      "It's where we log IEP goal progress, behavior incidents, and student",
      "support plans in one place, so the teacher, para, SLP, OT/PT, and",
      "behavior specialist all see the same data.",
      "",
      `Sign in here: ${appUrl}`,
      "",
      "You'll be prompted to fill in your name and role the first time you",
      "sign in — that's how your data points get attributed on shared",
      "reports.",
      "",
      "If you don't have an account yet, let me know your email and I'll",
      "have our admin add you.",
      "",
      "Thanks!",
    ].join('\n');
  }, [appUrl]);

  const copy = async (key, text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / non-secure contexts.
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(key);
      setTimeout(() => setCopied((prev) => (prev === key ? null : prev)), 1600);
    } catch {
      // If clipboard is blocked, at least select the text below so the
      // user can copy manually.
    }
  };

  return (
    <section className="bg-card rounded-xl border border-border overflow-hidden">
      <header className="p-5 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="UserPlus" size={18} className="text-primary" />
          <h2 className="text-base font-semibold text-card-foreground">
            Invite a teammate
          </h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Add the OT, PT, speech therapist, behavior specialist, or para who
          works with your students. Everyone on the workspace sees the same
          data live.
        </p>
      </header>

      <div className="p-5 space-y-5">
        <ol className="text-sm text-card-foreground space-y-3">
          <li className="flex gap-3">
            <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
              1
            </span>
            <div>
              <p className="font-medium">Your admin creates the account</p>
              <p className="text-muted-foreground text-xs mt-0.5">
                {isLive
                  ? "In Supabase → Authentication → Users, click Add user. Enter their email and a temporary password."
                  : "In demo mode there's nothing to create — but this is where your district admin would add the teammate in Supabase."}
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
              2
            </span>
            <div>
              <p className="font-medium">Share the sign-in link</p>
              <p className="text-muted-foreground text-xs mt-0.5">
                Send them the URL below. On first sign-in they'll be asked
                for their name and role so their data points show up
                correctly on shared reports.
              </p>
            </div>
          </li>
        </ol>

        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-1">
            Sign-in URL
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate text-sm text-card-foreground">
              {appUrl}
            </code>
            <Button
              variant="outline"
              size="sm"
              iconName={copied === 'url' ? 'Check' : 'Copy'}
              iconPosition="left"
              iconSize={14}
              onClick={() => copy('url', appUrl)}
            >
              {copied === 'url' ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-border">
          <div className="flex items-center justify-between px-3 pt-3">
            <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
              Ready-to-send email
            </p>
            <Button
              variant="ghost"
              size="sm"
              iconName={copied === 'email' ? 'Check' : 'Copy'}
              iconPosition="left"
              iconSize={14}
              onClick={() => copy('email', emailTemplate)}
            >
              {copied === 'email' ? 'Copied' : 'Copy email'}
            </Button>
          </div>
          <pre className="whitespace-pre-wrap text-xs leading-relaxed font-sans text-card-foreground p-3 max-h-52 overflow-y-auto">
{emailTemplate}
          </pre>
        </div>
      </div>
    </section>
  );
};

export default InvitePanel;
